import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AppRole } from '@/lib/types';
import { queryClient } from '@/App';
import { setUser as setSentryUser, addBreadcrumb } from '@/lib/sentry';
import { toast } from 'sonner';

export type ExperienceLevel = 'beginner' | 'intermediate';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roleLoading: boolean;
  role: AppRole | null;
  isAdmin: boolean;
  isTeacher: boolean;
  username: string | null;
  experienceLevel: ExperienceLevel | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, username?: string, voicePreference?: string, experienceLevel?: ExperienceLevel) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const [role, setRole] = useState<AppRole | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | null>(null);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username, experience_level')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      
      setUsername(data?.username ?? null);
      setExperienceLevel((data?.experience_level as ExperienceLevel) ?? 'beginner');
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchUserRole = async (userId: string) => {
    setRoleLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching user role:', error);
        setRole('user'); // Default to user role on error
        return;
      }
      
      setRole(data?.role as AppRole || 'user');
    } catch (error) {
      console.error('Error fetching user role:', error);
      setRole('user'); // Default to user role on error
    } finally {
      setRoleLoading(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (import.meta.env.DEV) {
          console.log('Auth state changed:', event, session?.user?.email);
        }
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false); // Always set loading to false when auth state changes
        
        // Defer data fetch with setTimeout to prevent deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserRole(session.user.id);
            fetchUserProfile(session.user.id);
          }, 0);
          
          // Set Sentry user context (without email for privacy)
          setSentryUser(session.user.id);
          addBreadcrumb('auth', `User ${event}`, { userId: session.user.id });
        } else {
          setRole(null);
          setUsername(null);
          setExperienceLevel(null);
          
          // Clear Sentry user context on logout
          setSentryUser(null);
          addBreadcrumb('auth', 'User signed out');
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (import.meta.env.DEV) {
        console.log('Initial session check:', session?.user?.email);
      }
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserRole(session.user.id);
        fetchUserProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, username?: string, voicePreference?: string, expLevel?: ExperienceLevel) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          username: username || undefined,
          arabic_voice_preference: voicePreference || undefined,
          experience_level: expLevel || 'beginner',
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Clear ALL query cache before signing out to prevent stale data for next user
    queryClient.clear();

    // IMPORTANT: Prefer local sign-out so logout still works even if network requests hang
    // (e.g. flaky Wi‑Fi, captive portals, aggressive extensions)
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Local sign out error:', error);
    }

    // Best-effort global sign-out (don’t block UI)
    try {
      void supabase.auth.signOut();
    } catch {
      // ignore
    }

    // Always clear local state
    setUser(null);
    setSession(null);
    setRole(null);
    setUsername(null);
    setExperienceLevel(null);
  };

  // Admin-triggered "force logout": every client will log itself out on next check
  const lastForceLogoutCheckAt = useRef<number>(0);
  const lastScheduledLogoutCheckAt = useRef<string>(''); // Track last checked date to avoid double logout

  const decodeJwtIatMs = (jwt: string): number | null => {
    try {
      const payloadPart = jwt.split('.')[1];
      if (!payloadPart) return null;

      // base64url -> base64
      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(base64);
      const payload = JSON.parse(json) as { iat?: number };
      return typeof payload.iat === 'number' ? payload.iat * 1000 : null;
    } catch {
      return null;
    }
  };

  // Check if it's around 3 AM German time (CET/CEST)
  const isScheduledLogoutTime = (): boolean => {
    const now = new Date();
    // Get German time using Intl API
    const germanTime = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: 'numeric',
      minute: 'numeric',
    }).formatToParts(now);
    
    const hour = parseInt(germanTime.find(p => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(germanTime.find(p => p.type === 'minute')?.value || '0', 10);
    
    // 3:00 - 3:15 AM German time window
    return hour === 3 && minute < 15;
  };

  const getGermanDateString = (): string => {
    return new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  };

  const checkForceLogout = async (currentSession: Session) => {
    // Throttle checks (avoid hammering the DB)
    const now = Date.now();
    if (now - lastForceLogoutCheckAt.current < 15000) return;
    lastForceLogoutCheckAt.current = now;

    // Check scheduled 3 AM logout (German time)
    const todayGerman = getGermanDateString();
    if (isScheduledLogoutTime() && lastScheduledLogoutCheckAt.current !== todayGerman) {
      lastScheduledLogoutCheckAt.current = todayGerman;
      console.log('[Auth] Scheduled 3 AM logout triggered for cache refresh');
      toast.message('Automatische Aktualisierung um 3 Uhr – bitte neu anmelden.', {
        description: 'Die App wurde aktualisiert, um optimale Leistung zu gewährleisten.',
      });
      
      // Clear caches before logout
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        } catch (e) {
          console.error('Cache clear error:', e);
        }
      }
      
      await signOut();
      return;
    }

    const sessionIatMs = decodeJwtIatMs(currentSession.access_token);
    if (!sessionIatMs) return;

    const { data, error } = await supabase
      .from('app_settings')
      .select('force_logout_at')
      .eq('id', 1)
      .maybeSingle();

    if (error || !data?.force_logout_at) return;

    const forceLogoutAtMs = new Date(data.force_logout_at).getTime();

    // If admin triggered logout after this session was issued -> logout
    if (forceLogoutAtMs > sessionIatMs) {
      toast.message('Du wurdest ausgeloggt (Admin: Sessions zurückgesetzt).');
      await signOut();
    }
  };

  useEffect(() => {
    if (!session?.access_token) return;

    let cancelled = false;

    const run = async () => {
      if (cancelled || !session) return;
      try {
        await checkForceLogout(session);
      } catch {
        // silent
      }
    };

    // initial + on focus
    run();
    window.addEventListener('focus', run);

    // periodic
    const interval = window.setInterval(run, 30000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', run);
      window.clearInterval(interval);
    };
  }, [session?.access_token]);

  const refreshProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  };

  const isAdmin = role === 'admin';
  const isTeacher = role === 'teacher';

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        roleLoading,
        role,
        isAdmin,
        isTeacher,
        username,
        experienceLevel,
        signIn,
        signUp,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
